import React, { useContext, memo, useEffect } from 'react';
import { isEmpty } from 'ramda';
import { useLocation } from 'react-router';
import DBRouter from '../DBRouter/DBRouter';
import { ActiveDatabaseContext } from '../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import DataErrorPage from '../../../Components/DataErrorPage/DataErrorPage';
import { useLoginStateContext } from '../../Login/Contexts/LoginStateContext/LoginStateContext';
import { System, useBaseRouteContext } from '../Utils/routerUtils';
import { maybeRedirectToDatabase } from '../../../Utils/databaseRedirect';

interface IProps {}

const BaseRouter: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const databaseContext = useContext(ActiveDatabaseContext);
  const [{ database }, setBaseRoute] = useBaseRouteContext();
  const availableRoutines = databaseContext.availableRoutines;
  const loginContext = useLoginStateContext();
  const noRoutinesAvailable =
    isEmpty(availableRoutines) && !databaseContext.activeDatabaseInfoLoading;

  const loggedInToOtherDatabase =
    loginContext.isLoggedIn &&
    !loginContext.loading &&
    !loginContext.databases.some(db => db.name === database);

  const location = useLocation();
  // Om vi är på en route utan databas men inloggad i någon databas så byter vi till databasen vi är inloggade i
  useEffect(() => {
    if (loggedInToOtherDatabase && !database) {
      const database = loginContext.databases.at(0);
      setBaseRoute({
        database: database?.name,
        system: (database?.typ ?? '').startsWith('GAS')
          ? System.GAS
          : System.Lokalt,
        username: loginContext.sign,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInToOtherDatabase]);
  // Om vi är inloggade i fel miljö så redirectar vi
  useEffect(() => {
    maybeRedirectToDatabase(loginContext.databases, database, {
      performRedirect: true,
      pathname: location.pathname,
    });
  }, [loginContext.databases, database, location.pathname]);

  if (noRoutinesAvailable) {
    return (
      <DataErrorPage
        errorText={`Du är inte behörig till någon rutin som levereras på Nya Aveny-plattformen${
          databaseContext.activeDatabase
            ? ` i databasen ${databaseContext.activeDatabase}`
            : '.'
        }`}
      />
    );
  }
  if (loggedInToOtherDatabase) {
    return (
      <DataErrorPage
        errorText={
          database
            ? `Du är inte inloggad i databasen ${database}`
            : 'Ingen databas vald'
        }
      />
    );
  }
  if (loginContext.error) {
    return (
      <DataErrorPage
        errorText={`Fel vid hämtning av inloggningsstatus. ${loginContext.error.message}.`}
      />
    );
  }
  return <DBRouter />;
};

export default memo(BaseRouter);
